import React, { useState, useEffect, useRef } from "react";
import { useLanguage } from "../utils/i18n";

export default function PdfPanel({ book, onHide, onUpload, onReupload, books, onSelectBook, onDeleteBook, canSaveDocs, uploadProgress }) {
  const [iframeSrc, setIframeSrc] = useState(null);
  const [loadError, setLoadError] = useState(false);
  // view: "landing" | "paste" | "book-list" | "doc"
  const [view, setView] = useState("landing");
  const pasteRef = useRef(null);
  const [pasteEmpty, setPasteEmpty] = useState(true);
  const fileRef = useRef(null);
  const { t, lang } = useLanguage();

  // Auto-switch to doc viewer when a book is loaded
  useEffect(() => {
    if (book) setView("doc");
  }, [book?.id]);

  useEffect(() => {
    if (!book?.pdfUrl) { setIframeSrc(null); return; }
    setIframeSrc(null);
    setLoadError(false);
    fetch(book.pdfUrl)
      .then((r) => r.json())
      .then((data) => {
        if (data.url) {
          const ft = data.fileType || book.fileType || "pdf";
          setIframeSrc(
            ft === "pdf"
              ? data.url + "#zoom=page-fit&navpanes=0"
              : `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(data.url)}`
          );
        } else {
          setLoadError(true);
        }
      })
      .catch(() => setLoadError(true));
  }, [book?.pdfUrl]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && onUpload) onUpload(file);
    e.target.value = "";
  };

  const docCount = books?.length ?? 0;

  return (
    <div className="pdf-panel">
      <div className="pdf-main-area">

        {/* ── Upload progress overlay ── */}
        {uploadProgress !== null && (
          <div className="upload-progress-overlay">
            <div className="upload-progress-box">
              {uploadProgress === "processing" ? (
                <>
                  <div className="upload-progress-filename">{t("processingLabel")}</div>
                  <div className="upload-progress-bar-track">
                    <div className="upload-progress-bar-indeterminate" />
                  </div>
                </>
              ) : (
                <>
                  <div className="upload-progress-filename">{t("uploadingLabel")}</div>
                  <div className="upload-progress-bar-track">
                    <div
                      className="upload-progress-bar-fill"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <div className="upload-progress-pct">{uploadProgress}%</div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Toolbar ── */}
        <div className="pdf-toolbar">
          <button
            className={`pdf-mode-tab${view === "landing" ? " active" : ""}`}
            onClick={async () => {
              if (view === "landing") return;
              if (view === "doc" && book) {
                const ok = await onReupload();
                if (!ok) return;
              }
              setView("landing");
            }}
            title="Upload a document"
          >
            {t("uploadTab")}
          </button>
          <div className="pdf-mode-tabs">
            <button
              className={`pdf-mode-tab${view === "book-list" ? " active" : ""}`}
              onClick={async () => {
                if (view === "doc" && book) {
                  const ok = await onReupload();
                  if (!ok) return;
                }
                setView("book-list");
              }}
            >
              {t("uploadedDocsTab")}
            </button>
          </div>
          <button className="pdf-hide-btn" style={{ marginLeft: "auto" }} onClick={onHide} title="Hide panel">
            {t("hidePanel")}
          </button>
        </div>

        {/* ── Hidden file input ── */}
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.docx,.pptx"
          hidden
          onChange={handleFileChange}
        />

        {/* ── Landing: 3 choice cards ── */}
        {view === "landing" && (
          <div className="pdf-landing">
            <p className="pdf-landing-title">{t("getStartedTitle")}</p>
            <div className="pdf-landing-cards">
              <button className="pdf-landing-card" onClick={() => fileRef.current.click()}>
                <span className="pdf-landing-card-icon">📤</span>
                <span className="pdf-landing-card-label">{t("uploadDocLabel")}</span>
                <span className="pdf-landing-card-hint">PDF, Word, PowerPoint</span>
              </button>
              {canSaveDocs && (
                <button
                  className="pdf-landing-card"
                  onClick={() => setView("book-list")}
                  disabled={docCount === 0}
                >
                  <span className="pdf-landing-card-icon">📚</span>
                  <span className="pdf-landing-card-label">{t("uploadedDocsLabel")}</span>
                  <span className="pdf-landing-card-hint">
                    {docCount > 0
                      ? lang === "zh" ? `${docCount} 个文档可用` : `${docCount} doc${docCount !== 1 ? "s" : ""} available`
                      : t("noDocsYet")}
                  </span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Paste / Scratch-pad area ── (hidden) */}
        {false && view === "paste" && (
          <div className="paste-panel">
            <div className="paste-panel-header">
              <span className="paste-panel-hint">Paste text or images here (Ctrl+V / ⌘V)</span>
              <button
                className="paste-clear-btn"
                disabled={pasteEmpty}
                onClick={() => {
                  if (pasteRef.current) pasteRef.current.innerHTML = "";
                  setPasteEmpty(true);
                }}
              >
                Clear
              </button>
            </div>
            <div
              ref={pasteRef}
              className="paste-area"
              contentEditable
              suppressContentEditableWarning
              spellCheck={false}
              onInput={() =>
                setPasteEmpty(
                  !pasteRef.current?.textContent?.trim() &&
                  !pasteRef.current?.querySelector("img")
                )
              }
              data-placeholder="Paste anything here…"
            />
          </div>
        )}

        {/* ── Uploaded docs list ── */}
        {view === "book-list" && (
          <div className="pdf-book-list">
            <p className="pdf-book-list-title">{t("yourUploadedDocs")}</p>
            {docCount === 0 ? (
              <p className="pdf-book-list-empty">{t("noDocsUploaded")}</p>
            ) : (
              books.map((b) => (
                <div key={b.id} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <button
                    className="pdf-book-list-item"
                    style={{ flex: 1 }}
                    onClick={() => onSelectBook(b.id)}
                  >
                    <span className="pdf-book-list-icon">
                      {b.filename?.endsWith(".pptx") ? "📊" : b.filename?.endsWith(".docx") ? "📝" : "📄"}
                    </span>
                    <span className="pdf-book-list-name">{b.filename}</span>
                    <span className="pdf-book-list-pages">{b.total_pages}p</span>
                  </button>
                  {onDeleteBook && (
                    <button
                      title="Delete this document"
                      onClick={() => onDeleteBook(b.id)}
                      style={{
                        flexShrink: 0, background: "none", border: "1px solid #fca5a5",
                        borderRadius: 6, padding: "4px 8px", cursor: "pointer",
                        color: "#dc2626", fontSize: "0.85rem", lineHeight: 1,
                      }}
                    >
                      🗑
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* ── Document viewer ── */}
        {view === "doc" && (
          <>
            {!canSaveDocs && book && (
              <div className="session-warning-banner">
                {t("sessionWarning")}
              </div>
            )}
            {!book ? (
              <div className="pdf-placeholder">
                <p style={{ color: "var(--text-secondary)" }}>{t("noDocLoaded")}</p>
              </div>
            ) : loadError ? (
              <div className="pdf-placeholder">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <p>{t("couldNotLoad")}</p>
                <span className="hint">{t("backendHint")}</span>
              </div>
            ) : !iframeSrc ? (
              <div className="pdf-placeholder">
                <p style={{ color: "var(--text-secondary)" }}>Loading…</p>
              </div>
            ) : (
              <div style={{ flex: 1, overflow: "auto", WebkitOverflowScrolling: "touch", minHeight: 0 }}>
                <iframe
                  name="pdf-viewer"
                  title="PDF Viewer"
                  src={iframeSrc}
                  style={{ display: "block", width: "100%", height: "100%", border: "none", minHeight: "100vh" }}
                />
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
}

